import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined"
import Alert from "@mui/material/Alert"
import Autocomplete from "@mui/material/Autocomplete"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Chip from "@mui/material/Chip"
import CircularProgress from "@mui/material/CircularProgress"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import Grid from "@mui/material/Grid"
import Link from "@mui/material/Link"
import Paper from "@mui/material/Paper"
import Step from "@mui/material/Step"
import StepLabel from "@mui/material/StepLabel"
import Stepper from "@mui/material/Stepper"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import PropTypes from "prop-types"
import { useCallback, useEffect, useMemo, useState } from "react"

import Column from "../../../common/components/Column"
import Row from "../../../common/components/Row"
import { adminApi } from "../axios"
import BookingQuestionFields from "./BookingQuestionFields"
import {
  BOOKING_STEPS,
  MEETING_DURATION_MINUTES,
  buildBookingPayload,
  candidateToFormFields,
  copyText,
  emptyBookingForm,
  formatMeetingDateTime,
  formatSlotTime,
  getApiErrorMessage,
  getTimeZoneOptions,
  isReauthorizeError,
  meetingCandidateNames,
  meetingDetailsText,
  questionErrorKey,
  validateBookingStep,
  withAnswer,
} from "./meetingUtils"

export const APPOINTMENT_TITLE = "Quick Brokerage Introduction (10-minute call)"
const APPOINTMENT_DESCRIPTION =
  "This brief conversation will offer an opportunity to introduce you to our brokerage and identify any gaps or missed opportunities in your real estate business that our brokerage could enhance."
const SEARCH_DEBOUNCE_MS = 300

const STEP_SELECT_USER = 0
const STEP_DATE_TIME = 1
const STEP_DETAILS = 2
const STEP_CONFIRMED = 3

function AppointmentCard({ hostName }) {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2">
        {APPOINTMENT_TITLE}
        {hostName ? ` with ${hostName}` : ""}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {APPOINTMENT_DESCRIPTION}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        Call length: approximately 10 minutes. Telephone call.
      </Typography>
    </Paper>
  )
}

AppointmentCard.propTypes = { hostName: PropTypes.string }

export default function BookMeetingDialog({ open, onClose, onBooked }) {
  const [step, setStep] = useState(STEP_SELECT_USER)
  const [form, setForm] = useState(emptyBookingForm)
  const [errors, setErrors] = useState({})
  const [hostInput, setHostInput] = useState("")
  const [hostOptions, setHostOptions] = useState([])
  const [hostsLoading, setHostsLoading] = useState(false)
  const [availability, setAvailability] = useState(null)
  const [availabilityLoading, setAvailabilityLoading] = useState(false)
  const [availabilityError, setAvailabilityError] = useState("")
  const [candidateInput, setCandidateInput] = useState("")
  const [candidateOptions, setCandidateOptions] = useState([])
  // The questions the booking form asks for this user; null until loaded.
  const [questions, setQuestions] = useState(null)
  const [questionsError, setQuestionsError] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState("")
  const [bookedMeeting, setBookedMeeting] = useState(null)
  const [copyState, setCopyState] = useState("")
  // Where the time zone came from, for example "phone number +91...". Cleared
  // when the admin picks a zone themselves.
  const [timeZoneHint, setTimeZoneHint] = useState("")
  // The zones staff manage under Meetings > Time zones. Null until loaded, or
  // when they could not be loaded, and then every zone is offered.
  const [bookingZones, setBookingZones] = useState(null)
  const baseTimeZoneOptions = useMemo(
    () =>
      bookingZones?.length
        ? bookingZones.map((zone) => zone.time_zone)
        : getTimeZoneOptions(),
    [bookingZones]
  )
  const timeZoneOptions = baseTimeZoneOptions.includes(form.timeZone)
    ? baseTimeZoneOptions
    : [form.timeZone, ...baseTimeZoneOptions]
  const timeZoneLabels = useMemo(
    () =>
      Object.fromEntries(
        (bookingZones || []).map((zone) => [zone.time_zone, zone.label])
      ),
    [bookingZones]
  )

  const hostId = form.host?.id
  const { date, timeZone } = form

  const resetAll = useCallback(() => {
    setStep(STEP_SELECT_USER)
    setForm(emptyBookingForm())
    setErrors({})
    setHostInput("")
    setAvailability(null)
    setAvailabilityError("")
    setCandidateInput("")
    setCandidateOptions([])
    setQuestions(null)
    setQuestionsError("")
    setSubmitError("")
    setBookedMeeting(null)
    setCopyState("")
  }, [])

  useEffect(() => {
    if (open) resetAll()
  }, [open, resetAll])

  // Load the managed zones each time the dialog opens, so one that was just added
  // is there.
  useEffect(() => {
    if (!open) return undefined
    let cancelled = false
    adminApi
      .fetchMeetingTimeZones({ active: "true" })
      .then(({ data }) => {
        if (cancelled) return
        const zones = data.results || []
        setBookingZones(zones)
        // Before a user is chosen, start from the first zone if the browser's own
        // zone is not one of them.
        setForm((current) =>
          current.host ||
          zones.length === 0 ||
          zones.some((zone) => zone.time_zone === current.timeZone)
            ? current
            : { ...current, timeZone: zones[0].time_zone }
        )
      })
      .catch(() => {
        if (!cancelled) setBookingZones(null)
      })
    return () => {
      cancelled = true
    }
  }, [open])

  function updateForm(changes) {
    setForm((current) => ({ ...current, ...changes }))
    setErrors((current) => {
      const next = { ...current }
      Object.keys(changes).forEach((key) => delete next[key])
      return next
    })
  }

  // Users to choose from (search is server-side).
  useEffect(() => {
    if (!open || step !== STEP_SELECT_USER) return undefined
    let cancelled = false
    const timer = setTimeout(() => {
      setHostsLoading(true)
      adminApi
        .fetchMeetingHosts({ q: hostInput, page_size: 30 })
        .then(({ data }) => {
          if (!cancelled) setHostOptions(data.results || [])
        })
        .catch(() => {
          if (!cancelled) setHostOptions([])
        })
        .finally(() => {
          if (!cancelled) setHostsLoading(false)
        })
    }, SEARCH_DEBOUNCE_MS)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [open, step, hostInput])

  // Slots for the chosen day, with the user's busy times removed.
  useEffect(() => {
    if (!open || step !== STEP_DATE_TIME || !hostId || !date) return undefined
    let cancelled = false
    setAvailabilityLoading(true)
    setAvailabilityError("")
    setAvailability(null)
    setForm((current) => ({ ...current, slot: null }))
    adminApi
      .fetchMeetingAvailability({
        user_id: hostId,
        date,
        timezone: timeZone,
        duration: MEETING_DURATION_MINUTES,
      })
      .then(({ data }) => {
        if (!cancelled) setAvailability(data)
      })
      .catch((error) => {
        if (!cancelled)
          setAvailabilityError(
            getApiErrorMessage(error, "Could not load available times.")
          )
      })
      .finally(() => {
        if (!cancelled) setAvailabilityLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, step, hostId, date, timeZone])

  // The booking form's questions, with the choices for this user (their team).
  useEffect(() => {
    if (!open || step !== STEP_DETAILS || !hostId) return undefined
    let cancelled = false
    setQuestionsError("")
    adminApi
      .fetchMeetingForm({ user_id: hostId })
      .then(({ data }) => {
        if (!cancelled) setQuestions(data.questions || [])
      })
      .catch((error) => {
        if (!cancelled)
          setQuestionsError(
            getApiErrorMessage(error, "Could not load the booking form.")
          )
      })
    return () => {
      cancelled = true
    }
  }, [open, step, hostId])

  // The user's candidates (search is server-side).
  useEffect(() => {
    if (!open || step !== STEP_DETAILS || !hostId) return undefined
    let cancelled = false
    const timer = setTimeout(() => {
      adminApi
        .fetchMeetingCandidates({ user_id: hostId, q: candidateInput })
        .then(({ data }) => {
          if (!cancelled) setCandidateOptions(data || [])
        })
        .catch(() => {
          if (!cancelled) setCandidateOptions([])
        })
    }, SEARCH_DEBOUNCE_MS)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [open, step, hostId, candidateInput])

  // Everything after the user depends on the user, so start those over.
  function handleHostChanged(host) {
    // The time zone starts from what the user's phone number or state says.
    setForm((current) => ({
      ...emptyBookingForm(),
      timeZone: host?.suggested_time_zone || current.timeZone,
      host,
    }))
    setTimeZoneHint(
      host?.suggested_time_zone && host.suggested_time_zone_source
        ? `Suggested from ${host.suggested_time_zone_source}. You can change it.`
        : ""
    )
    setErrors({})
    setAvailability(null)
    setCandidateOptions([])
    setQuestions(null)
    setQuestionsError("")
  }

  async function handleCopyDetails() {
    const copied = await copyText(meetingDetailsText(bookedMeeting))
    setCopyState(copied ? "copied" : "failed")
  }

  function handleNext() {
    const stepErrors = validateBookingStep(step, form)
    setErrors(stepErrors)
    if (Object.keys(stepErrors).length === 0) setStep(step + 1)
  }

  function handleBack() {
    setSubmitError("")
    setStep(step - 1)
  }

  function handleSubmit() {
    if (!questions) return
    const stepErrors = validateBookingStep(STEP_DETAILS, form, questions)
    setErrors(stepErrors)
    if (Object.keys(stepErrors).length > 0) return
    setIsSubmitting(true)
    setSubmitError("")
    adminApi
      .createMeeting(buildBookingPayload(form, questions))
      .then(({ data }) => {
        setBookedMeeting(data)
        setStep(STEP_CONFIRMED)
        onBooked?.(data)
      })
      .catch((error) => {
        const message = getApiErrorMessage(
          error,
          "The meeting could not be booked."
        )
        setSubmitError(
          isReauthorizeError(error) ? `${message} Nothing was saved.` : message
        )
        if (error?.response?.data?.code === "meeting_conflict") {
          // The user already has a meeting then: pick another time. Going back
          // reloads the times, and the taken one is no longer offered.
          setStep(STEP_DATE_TIME)
        }
      })
      .finally(() => setIsSubmitting(false))
  }

  function handleAnswerChange(question, value) {
    setForm((current) => withAnswer(current, question, value))
    setErrors((current) => {
      const next = { ...current }
      delete next[questionErrorKey(question)]
      return next
    })
  }

  function textField(field, label, extra = {}) {
    return (
      <TextField
        label={label}
        size="small"
        fullWidth
        value={form[field]}
        onChange={(event) => updateForm({ [field]: event.target.value })}
        error={Boolean(errors[field])}
        helperText={errors[field]}
        {...extra}
      />
    )
  }

  function renderSelectUser() {
    return (
      <Column gap={2}>
        <Autocomplete
          options={hostOptions}
          loading={hostsLoading}
          value={form.host}
          onChange={(_, value) => handleHostChanged(value)}
          onInputChange={(_, value, reason) => {
            if (reason === "input") setHostInput(value)
          }}
          filterOptions={(options) => options}
          getOptionLabel={(option) => `${option.name} (${option.email})`}
          getOptionDisabled={(option) => !option.connected}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <Row
                sx={{
                  width: "100%",
                  justifyContent: "space-between",
                  gap: 1,
                }}
              >
                <span>
                  {option.name} ({option.email})
                </span>
                <Chip
                  size="small"
                  variant="outlined"
                  color={option.connected ? "success" : "default"}
                  label={
                    option.connected
                      ? option.provider === "ms"
                        ? "Outlook"
                        : "Google"
                      : "No calendar"
                  }
                />
              </Row>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="User"
              placeholder="Search by name or email"
              error={Boolean(errors.host)}
              helperText={
                errors.host ||
                "Users without a connected Google or Outlook calendar cannot be selected."
              }
            />
          )}
        />
        <AppointmentCard hostName={form.host?.name} />
      </Column>
    )
  }

  function renderDateTime() {
    return (
      <Column gap={2}>
        <AppointmentCard hostName={form.host?.name} />
        {submitError ? <Alert severity="warning">{submitError}</Alert> : null}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={5}>
            <TextField
              label="Date"
              type="date"
              size="small"
              fullWidth
              value={form.date}
              onChange={(event) => updateForm({ date: event.target.value })}
              InputLabelProps={{ shrink: true }}
              error={Boolean(errors.date)}
              helperText={errors.date}
            />
          </Grid>
          <Grid item xs={12} sm={7}>
            <Autocomplete
              disableClearable
              size="small"
              options={timeZoneOptions}
              getOptionLabel={(zone) =>
                timeZoneLabels[zone]
                  ? `${timeZoneLabels[zone]} (${zone})`
                  : zone
              }
              value={form.timeZone}
              onChange={(_, value) => {
                setTimeZoneHint("")
                updateForm({ timeZone: value })
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Time zone"
                  helperText={timeZoneHint || undefined}
                />
              )}
            />
          </Grid>
        </Grid>
        {availabilityLoading ? (
          <Row sx={{ justifyContent: "center", py: 2 }}>
            <CircularProgress size={24} />
          </Row>
        ) : null}
        {availabilityError ? (
          <Alert severity="error">{availabilityError}</Alert>
        ) : null}
        {availability && !availability.busy_known ? (
          <Alert severity="warning">
            This user&apos;s calendar could not be checked, so these times may
            overlap an existing meeting.
          </Alert>
        ) : null}
        {availability ? (
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Available times (9:00 AM – 5:00 PM, {availability.timezone})
            </Typography>
            <Grid container spacing={1}>
              {availability.slots.map((slot) => (
                <Grid item xs={4} sm={3} key={slot.start}>
                  <Button
                    fullWidth
                    size="small"
                    variant={
                      form.slot?.start === slot.start ? "contained" : "outlined"
                    }
                    disabled={!slot.available}
                    onClick={() => updateForm({ slot })}
                  >
                    {formatSlotTime(slot, availability.timezone)}
                  </Button>
                </Grid>
              ))}
            </Grid>
          </Box>
        ) : null}
        {errors.slot ? (
          <Typography variant="caption" color="error">
            {errors.slot}
          </Typography>
        ) : null}
      </Column>
    )
  }

  function renderDetails() {
    return (
      <Column gap={2}>
        <Typography variant="body2" color="text.secondary">
          {formatMeetingDateTime(form.slot?.start, form.timeZone)} with{" "}
          {form.host?.name}
        </Typography>
        <Autocomplete
          options={candidateOptions}
          value={form.candidate}
          onChange={(_, value) =>
            value
              ? updateForm(candidateToFormFields(value))
              : updateForm({ candidate: null })
          }
          onInputChange={(_, value, reason) => {
            if (reason === "input") setCandidateInput(value)
          }}
          filterOptions={(options) => options}
          getOptionLabel={(option) =>
            [option.candidate_name, option.email].filter(Boolean).join(" · ")
          }
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label="Existing candidate (optional)"
              helperText="Pick one of this user's candidates, or fill in the details below to create a new one."
            />
          )}
        />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            {textField("firstName", "First name *")}
          </Grid>
          <Grid item xs={12} sm={6}>
            {textField("lastName", "Last name *")}
          </Grid>
          <Grid item xs={12} sm={6}>
            {textField("phone", "Phone *")}
          </Grid>
          <Grid item xs={12} sm={6}>
            {textField("email", "Email *", { type: "email" })}
          </Grid>
        </Grid>
        <Typography variant="subtitle2">Agent booking form</Typography>
        {questionsError ? (
          <Alert severity="error">{questionsError}</Alert>
        ) : null}
        {questions === null && !questionsError ? (
          <Row sx={{ justifyContent: "center", py: 2 }}>
            <CircularProgress size={24} aria-label="Loading the booking form" />
          </Row>
        ) : null}
        {questions ? (
          <BookingQuestionFields
            questions={questions}
            form={form}
            errors={errors}
            onAnswerChange={handleAnswerChange}
          />
        ) : null}
        {submitError ? <Alert severity="error">{submitError}</Alert> : null}
      </Column>
    )
  }

  function renderConfirmed() {
    const detailsText = meetingDetailsText(bookedMeeting)
    return (
      <Column gap={2}>
        <Alert severity="success">Appointment confirmed.</Alert>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2">{bookedMeeting?.title}</Typography>
          <Typography variant="body2">
            {formatMeetingDateTime(bookedMeeting?.start_datetime)} (your time
            zone)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Telephone call with {meetingCandidateNames(bookedMeeting)}. An
            invitation was sent from {bookedMeeting?.host?.email}&apos;s
            calendar.
          </Typography>
          {bookedMeeting?.event_link ? (
            <Link
              href={bookedMeeting.event_link}
              target="_blank"
              rel="noopener noreferrer"
              variant="body2"
              sx={{ display: "inline-block", mt: 1 }}
            >
              Open in calendar
            </Link>
          ) : null}
        </Paper>
        {detailsText ? (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Row
              sx={{
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Typography variant="subtitle2">Booking form / notes</Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={<ContentCopyOutlinedIcon />}
                onClick={handleCopyDetails}
              >
                Copy details
              </Button>
            </Row>
            <Typography
              variant="body2"
              component="pre"
              sx={{
                m: 0,
                mt: 1,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontFamily: "inherit",
              }}
            >
              {detailsText.replace(/^Booking form \/ notes\n/, "")}
            </Typography>
            {copyState === "copied" ? (
              <Typography variant="caption" color="success.main">
                Details copied.
              </Typography>
            ) : null}
            {copyState === "failed" ? (
              <Typography variant="caption" color="error">
                Could not copy automatically. Select the details above and copy
                them.
              </Typography>
            ) : null}
          </Paper>
        ) : null}
      </Column>
    )
  }

  const stepRenderers = [
    renderSelectUser,
    renderDateTime,
    renderDetails,
    renderConfirmed,
  ]

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Book a meeting</DialogTitle>
      <DialogContent dividers>
        <Stepper activeStep={step} alternativeLabel sx={{ mb: 3 }}>
          {BOOKING_STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        {stepRenderers[step]()}
      </DialogContent>
      <DialogActions>
        {step === STEP_CONFIRMED ? (
          <>
            <Button onClick={resetAll}>Book another</Button>
            <Button onClick={onClose} variant="contained">
              Close
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            {step > STEP_SELECT_USER ? (
              <Button onClick={handleBack} disabled={isSubmitting}>
                Back
              </Button>
            ) : null}
            {step === STEP_DETAILS ? (
              <Button
                onClick={handleSubmit}
                variant="contained"
                disabled={isSubmitting || !questions}
              >
                {isSubmitting ? "Booking..." : "Confirm appointment"}
              </Button>
            ) : (
              <Button onClick={handleNext} variant="contained">
                Next
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}

BookMeetingDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onBooked: PropTypes.func,
}
